import { useFrame } from '@react-three/fiber'
import { useRef } from 'react'
import type * as THREE from 'three'
import { CaixaArredondada } from '../componentes/CaixaArredondada'

/**
 * 1. Caminhão de Bombeiros — Auto Bomba Tanque / Salvamento (ABT)
 *
 * Implementa a viatura pesada de proteção rodoviária:
 * - Corpo em vermelho bombeiro (#dc2626) com detalhes e faixas refletivas
 * - Cabine dupla (crew cab) com para-brisa, janelas fumê e grade dianteira
 * - Para-choque reforçado em aço escuro com guincho mecânico
 * - Baú de ferramentas/equipamentos com persianas de enrolar de alumínio anodizado
 * - Escada de resgate e canhão monitor/mangotinho no teto
 * - Traseira com faixas chevron refletivas de segurança viária (45° amarelo e vermelho)
 * - Rodas dianteiras esterçadas para fora da pista (doutrina operacional: deflexão segura)
 * - Giroflex com barra de iluminação de emergência (LEDs estroboscópicos pulsantes)
 */
export function CaminhaoBombeirosTabletop() {
  const redLightRef = useRef<THREE.MeshStandardMaterial>(null)
  const blueLightRef = useRef<THREE.MeshStandardMaterial>(null)
  const redGlowRef = useRef<THREE.PointLight>(null)
  const blueGlowRef = useRef<THREE.PointLight>(null)

  useFrame(({ clock }) => {
    const t = clock.elapsedTime * 7
    const fase = Math.floor(t) % 4
    const redAtivo = fase === 0 || fase === 1
    const blueAtivo = fase === 2 || fase === 3

    if (redLightRef.current) {
      redLightRef.current.emissiveIntensity = redAtivo ? 3.5 : 0.2
    }
    if (blueLightRef.current) {
      blueLightRef.current.emissiveIntensity = blueAtivo ? 3.5 : 0.2
    }
    if (redGlowRef.current) {
      redGlowRef.current.intensity = redAtivo ? 2.5 : 0
    }
    if (blueGlowRef.current) {
      blueGlowRef.current.intensity = blueAtivo ? 2.5 : 0
    }
  })

  return (
    <group position={[0, 0.55, 0]}>
      {/* --- A. CABINE DUPLA (Dianteira) --- */}
      <group position={[-1.75, 0.95, 0]}>
        {/* Bloco principal da cabine */}
        <mesh position={[0, 0, 0]}>
          <CaixaArredondada args={[2.2, 1.9, 2.15]} />
          <meshStandardMaterial color="#dc2626" roughness={0.35} metalness={0.2} />
        </mesh>

        {/* Teto branco da cabine (padrão de comando) */}
        <mesh position={[0, 0.96, 0]}>
          <CaixaArredondada args={[2.22, 0.06, 2.17]} />
          <meshStandardMaterial color="#f8fafc" roughness={0.4} />
        </mesh>

        {/* Para-brisa frontal fumê */}
        <mesh position={[-1.11, 0.35, 0]}>
          <CaixaArredondada args={[0.04, 0.82, 1.98]} />
          <meshStandardMaterial color="#0f172a" roughness={0.1} metalness={0.8} />
        </mesh>

        {/* Janelas laterais da cabine */}
        {[-1, 1].map((lado) => (
          <mesh key={lado} position={[-0.1, 0.35, lado * 1.08]}>
            <CaixaArredondada args={[1.7, 0.72, 0.04]} />
            <meshStandardMaterial color="#0f172a" roughness={0.1} metalness={0.8} />
          </mesh>
        ))}

        {/* Grade frontal e faróis */}
        <mesh position={[-1.12, -0.32, 0]}>
          <CaixaArredondada args={[0.03, 0.55, 1.7]} />
          <meshStandardMaterial color="#1e293b" roughness={0.8} />
        </mesh>
        {[-0.65, 0.65].map((z) => (
          <mesh key={z} position={[-1.13, -0.22, z]} rotation={[0, 0, Math.PI / 2]}>
            <cylinderGeometry args={[0.12, 0.12, 0.04, 16]} />
            <meshStandardMaterial color="#fef08a" emissive="#fef08a" emissiveIntensity={0.8} />
          </mesh>
        ))}

        {/* Para-choque dianteiro em aço escuro com guincho */}
        <mesh position={[-1.22, -0.68, 0]}>
          <CaixaArredondada args={[0.3, 0.4, 2.25]} />
          <meshStandardMaterial color="#0f172a" roughness={0.7} />
        </mesh>
        <mesh position={[-1.38, -0.68, 0]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.14, 0.14, 0.45, 16]} />
          <meshStandardMaterial color="#64748b" metalness={0.7} />
        </mesh>

        {/* Retrovisores externos */}
        {[-1, 1].map((lado) => (
          <group key={lado} position={[-0.9, 0.35, lado * 1.18]}>
            <mesh>
              <CaixaArredondada args={[0.08, 0.38, 0.14]} />
              <meshStandardMaterial color="#0f172a" />
            </mesh>
          </group>
        ))}

        {/* --- GIROFLEX / LUZES DE EMERGÊNCIA (Teto da Cabine) --- */}
        <group position={[0, 1.05, 0]}>
          {/* Base aerodinâmica */}
          <mesh position={[0, 0, 0]}>
            <CaixaArredondada args={[0.4, 0.08, 1.85]} />
            <meshStandardMaterial color="#0f172a" />
          </mesh>

          {/* Lente estroboscópica Vermelha (lado esquerdo) */}
          <mesh position={[0, 0.08, -0.55]}>
            <CaixaArredondada args={[0.32, 0.12, 0.65]} />
            <meshStandardMaterial
              ref={redLightRef}
              color="#ef4444"
              emissive="#ef4444"
              emissiveIntensity={2.5}
              roughness={0.15}
            />
          </mesh>
          <pointLight ref={redGlowRef} color="#ef4444" distance={9} intensity={2.0} position={[0, 0.5, -0.8]} />

          {/* Centro âmbar/branco */}
          <mesh position={[0, 0.08, 0]}>
            <CaixaArredondada args={[0.26, 0.1, 0.32]} />
            <meshStandardMaterial color="#f8fafc" emissive="#ffffff" emissiveIntensity={1.2} />
          </mesh>

          {/* Lente estroboscópica Azul (lado direito) */}
          <mesh position={[0, 0.08, 0.55]}>
            <CaixaArredondada args={[0.32, 0.12, 0.65]} />
            <meshStandardMaterial
              ref={blueLightRef}
              color="#3b82f6"
              emissive="#3b82f6"
              emissiveIntensity={2.5}
              roughness={0.15}
            />
          </mesh>
          <pointLight ref={blueGlowRef} color="#3b82f6" distance={9} intensity={2.0} position={[0, 0.5, 0.8]} />
        </group>
      </group>

      {/* --- B. BAÚ DE SALVAMENTO / TANQUE (Traseira) --- */}
      <group position={[1.25, 1.1, 0]}>
        {/* Carroceria principal do baú */}
        <mesh position={[0, 0, 0]}>
          <CaixaArredondada args={[3.8, 2.15, 2.2]} />
          <meshStandardMaterial color="#dc2626" roughness={0.35} metalness={0.2} />
        </mesh>

        {/* Persianas laterais de alumínio anodizado para equipamentos */}
        {[-1, 1].map((lado) => (
          <group key={lado} position={[0, -0.05, lado * 1.11]}>
            {[-1.15, 0, 1.15].map((x, idx) => (
              <mesh key={idx} position={[x, 0, 0]}>
                <CaixaArredondada args={[0.95, 1.65, 0.03]} />
                <meshStandardMaterial color="#cbd5e1" roughness={0.4} metalness={0.65} />
              </mesh>
            ))}
          </group>
        ))}

        {/* Faixa refletiva amarela lateral */}
        {[-1, 1].map((lado) => (
          <mesh key={lado} position={[0, 0.85, lado * 1.105]}>
            <CaixaArredondada args={[3.75, 0.1, 0.01]} />
            <meshStandardMaterial color="#fef08a" emissive="#fef08a" emissiveIntensity={0.5} />
          </mesh>
        ))}

        {/* Escada de resgate montada no teto do baú */}
        <group position={[0, 1.15, -0.55]}>
          <mesh position={[0, 0, 0]}>
            <CaixaArredondada args={[3.5, 0.08, 0.52]} />
            <meshStandardMaterial color="#94a3b8" metalness={0.7} roughness={0.3} />
          </mesh>
        </group>

        {/* Canhão monitor / canhão de água e carretel no teto */}
        <group position={[-1.1, 1.25, 0.45]}>
          <mesh position={[0, 0, 0]}>
            <cylinderGeometry args={[0.2, 0.22, 0.25, 12]} />
            <meshStandardMaterial color="#dc2626" />
          </mesh>
          <mesh position={[0.1, 0.2, 0]} rotation={[0, 0, -Math.PI / 4]}>
            <cylinderGeometry args={[0.07, 0.08, 0.35, 12]} />
            <meshStandardMaterial color="#e2e8f0" metalness={0.8} />
          </mesh>
        </group>

        {/* Traseira do caminhão com Chevron refletivo de segurança viária */}
        <mesh position={[1.91, -0.05, 0]}>
          <CaixaArredondada args={[0.04, 1.85, 2.1]} />
          <meshStandardMaterial color="#b91c1c" roughness={0.5} />
        </mesh>
        {/* Faixas zebradas de sinalização traseira */}
        {[-0.6, 0, 0.6].map((z, i) => (
          <mesh key={i} position={[1.92, -0.5, z]}>
            <CaixaArredondada args={[0.02, 0.25, 0.45]} />
            <meshStandardMaterial color="#facc15" emissive="#facc15" emissiveIntensity={0.4} />
          </mesh>
        ))}

        {/* Plataforma / degrau traseiro */}
        <mesh position={[2.05, -0.9, 0]}>
          <CaixaArredondada args={[0.35, 0.15, 2.15]} />
          <meshStandardMaterial color="#1e293b" metalness={0.7} />
        </mesh>
      </group>

      {/* --- C. EIXOS E RODAS --- */}
      {/* Eixo Dianteiro esterçado (rodas viradas para o acostamento/fora da pista) */}
      {[-1, 1].map((lado) => (
        <group key={`diant-${lado}`} position={[-1.85, 0, lado * 1.05]} rotation={[0, 0.35, 0]}>
          <mesh rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[0.48, 0.48, 0.28, 20]} />
            <meshStandardMaterial color="#0f172a" roughness={0.9} />
          </mesh>
          {/* Calota/Aro prateado */}
          <mesh position={[0, 0, lado * 0.1]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[0.26, 0.26, 0.1, 16]} />
            <meshStandardMaterial color="#94a3b8" metalness={0.6} />
          </mesh>
        </group>
      ))}

      {/* Eixos Traseiros com Rodas Duplas */}
      {[0.5, 2.1].map((x) =>
        [-1, 1].map((lado) => (
          <group key={`tras-${x}-${lado}`} position={[x, 0, lado * 1.05]}>
            <mesh rotation={[Math.PI / 2, 0, 0]}>
              <cylinderGeometry args={[0.48, 0.48, 0.38, 20]} />
              <meshStandardMaterial color="#0f172a" roughness={0.9} />
            </mesh>
            <mesh position={[0, 0, lado * 0.14]} rotation={[Math.PI / 2, 0, 0]}>
              <cylinderGeometry args={[0.26, 0.26, 0.12, 16]} />
              <meshStandardMaterial color="#94a3b8" metalness={0.6} />
            </mesh>
          </group>
        )),
      )}
    </group>
  )
}
